/**
 * useModuleUsage - Tracks module favorites (DB-backed) and usage frequency (localStorage).
 * Used by the ModuleSwitcher dropdown to show "Your Modules" (favorites + used)
 * vs "Other Modules" (never used, grayed).
 *
 * Favorites are persisted in user.preferences.module_favorites via the auth API,
 * so they sync across devices. Usage tracking stays in localStorage (high-frequency,
 * device-specific).
 *
 * On first load, any localStorage favorites are migrated to the DB and removed locally.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { useAuth } from '../auth';

const STORAGE_KEY_FAVORITES = 'panluma:module-favorites';
const STORAGE_KEY_USAGE = 'panluma:module-usage';
const USAGE_WINDOW_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

interface UsageEntry {
  moduleId: string;
  timestamp: number;
}

function loadLocalFavorites(): string[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_FAVORITES);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function loadUsage(): UsageEntry[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_USAGE);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
}

function saveUsage(entries: UsageEntry[]) {
  localStorage.setItem(STORAGE_KEY_USAGE, JSON.stringify(entries));
}

/** Prune entries older than the rolling window */
function pruneUsage(entries: UsageEntry[]): UsageEntry[] {
  const cutoff = Date.now() - USAGE_WINDOW_MS;
  return entries.filter((e) => e.timestamp > cutoff);
}

/** Count usage per module within the rolling window */
function getUsageCounts(entries: UsageEntry[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const e of entries) {
    counts[e.moduleId] = (counts[e.moduleId] || 0) + 1;
  }
  return counts;
}

export function useModuleUsage() {
  const { user, updateUser } = useAuth();
  const favorites: string[] = (user?.preferences?.module_favorites as string[]) ?? [];
  const [usageEntries, setUsageEntries] = useState<UsageEntry[]>(() => pruneUsage(loadUsage()));
  const migrationDone = useRef(false);

  // One-time migration: move localStorage favorites to DB, then clear local
  useEffect(() => {
    if (!user || migrationDone.current) return;
    migrationDone.current = true;

    const localFavs = loadLocalFavorites();
    if (localFavs.length === 0) return;

    const dbFavs = (user.preferences?.module_favorites as string[]) ?? [];
    const merged = Array.from(new Set([...dbFavs, ...localFavs]));

    // Only update if there are new favorites to add
    if (merged.length > dbFavs.length) {
      updateUser({
        preferences: { ...user.preferences, module_favorites: merged },
      }).then(() => {
        localStorage.removeItem(STORAGE_KEY_FAVORITES);
      }).catch(() => {
        // Keep local favorites if migration fails — will retry next load
      });
    } else {
      localStorage.removeItem(STORAGE_KEY_FAVORITES);
    }
  }, [user, updateUser]);

  // Persist usage whenever it changes
  useEffect(() => {
    saveUsage(usageEntries);
  }, [usageEntries]);

  /** Record a module visit (call on module switch) */
  const recordModuleVisit = useCallback((moduleId: string) => {
    setUsageEntries((prev) => {
      const pruned = pruneUsage(prev);
      return [...pruned, { moduleId, timestamp: Date.now() }];
    });
  }, []);

  /** Toggle favorite status for a module */
  const toggleFavorite = useCallback((moduleId: string) => {
    if (!user) return;
    const current = (user.preferences?.module_favorites as string[]) ?? [];
    const updated = current.includes(moduleId)
      ? current.filter((id) => id !== moduleId)
      : [...current, moduleId];
    updateUser({
      preferences: { ...user.preferences, module_favorites: updated },
    });
  }, [user, updateUser]);

  /** Check if a module is favorited */
  const isFavorite = useCallback((moduleId: string) => {
    return favorites.includes(moduleId);
  }, [favorites]);

  /** Get usage count for a module in the rolling window */
  const usageCounts = getUsageCounts(usageEntries);

  /** Check if a module has ever been visited (in current usage window) */
  const hasBeenUsed = useCallback((moduleId: string) => {
    return (usageCounts[moduleId] ?? 0) > 0;
  }, [usageCounts]);

  /** Get usage count for a module */
  const getUsageCount = useCallback((moduleId: string) => {
    return usageCounts[moduleId] ?? 0;
  }, [usageCounts]);

  /** Re-read usage from localStorage (favorites come from user state automatically) */
  const refresh = useCallback(() => {
    setUsageEntries(pruneUsage(loadUsage()));
  }, []);

  /** Sort modules: favorites first (by frequency), then used (by frequency), then unused (alphabetical) */
  const sortModules = useCallback(<T extends { id: string }>(
    modules: T[],
    getName: (m: T) => string
  ): { yourModules: T[]; otherModules: T[] } => {
    const used: T[] = [];
    const unused: T[] = [];

    for (const m of modules) {
      if ((usageCounts[m.id] ?? 0) > 0 || favorites.includes(m.id)) {
        used.push(m);
      } else {
        unused.push(m);
      }
    }

    used.sort((a, b) => {
      const aFav = favorites.includes(a.id);
      const bFav = favorites.includes(b.id);
      if (aFav && !bFav) return -1;
      if (!aFav && bFav) return 1;
      return (usageCounts[b.id] ?? 0) - (usageCounts[a.id] ?? 0);
    });

    unused.sort((a, b) => getName(a).localeCompare(getName(b)));

    return { yourModules: used, otherModules: unused };
  }, [usageCounts, favorites]);

  return {
    favorites,
    toggleFavorite,
    isFavorite,
    recordModuleVisit,
    hasBeenUsed,
    getUsageCount,
    usageCounts,
    refresh,
    sortModules,
  };
}
